import { mdiDeleteOutline, mdiMinus, mdiPencilOutline, mdiPlus, mdiRestore } from '@mdi/js';
import { Modal as ConfirmModal } from 'antd';
import type { FilterValue, ParsedConfigurationItem, TableQuery } from 'common/models';
import { useGetCompanyAndUserInfoQuery, useGetUserConfigurationQueryV2 } from 'common/queries';
import { useStorePermissions } from 'common/usePermissions';
import {
  Button,
  EventLogModal,
  Icon,
  Modal,
  MoreDropdown,
  PageContent,
  RenderSettingsDrawerWrapper,
  SearchBox,
  UserCustomButtons
} from 'components';
import useRecordRecentDetail from 'components/AppShell/useRecordRecentDetail';
import { PageContentHeading } from 'components/Layout';
import { processServerFormError } from 'helpers/errorHandling';
import {
  getParsedConfigurationItem,
  getRenderSettingsScopeTypeValue
} from 'helpers/renderSettings';
import { getTableQueryFromAntdTable, sizeChangerPaginationConfig } from 'helpers/tableHelpers';
import { useCustomTranslation } from 'helpers/translationHelper';
import type { StoreMaterialSortBy, StoreMaterialTableQuery } from 'pages/material/models';
import { STORE_MATERIAL_SORT_KEYS } from 'pages/material/models';
import {
  useDeleteStoreMutation,
  useEditStoreV2Mutation,
  useGetStoreDetailQuery
} from 'pages/material/queries';
import { useGetCodebooksQuery } from 'pages/settings/codebooks/queries';
import React, { useCallback, useEffect, useState } from 'react';

import MoveMaterialFromStoreModal from './components/operations/moveFrom/MoveMaterialFromStoreModal';
import MoveMaterialToStoreModal from './components/operations/moveTo/MoveMaterialToStoreModal';
import type { StoreFormData } from './components/StoreFormV2';
import StoreFormV2 from './components/StoreFormV2';
import StoreMaterialTable from './components/StoreMaterialTable';

export const defaultStoreMaterialTableQuery: StoreMaterialTableQuery = {
  page: 1,
  pageSize: 100
};

interface Props {
  activeStoreId: string;
}

const StoreDetail: React.FC<Props> = (props): React.ReactElement => {
  const { activeStoreId } = props;

  const { t } = useCustomTranslation();
  const codebooksQuery = useGetCodebooksQuery();
  const companyAndUserQuery = useGetCompanyAndUserInfoQuery();
  const { can } = useStorePermissions(activeStoreId);

  const editStoreV2Mutation = useEditStoreV2Mutation();
  const deleteStoreMutation = useDeleteStoreMutation();
  const configurationQuery = useGetUserConfigurationQueryV2();

  const configuration = configurationQuery.data;
  const appUser = companyAndUserQuery.data?.user;

  const [showEditStoreV2Modal, setShowEditStoreV2Modal] = useState(false);
  const [showIncomeMaterialModal, setShowIncomeMaterialModal] = useState(false);
  const [showOutcomeMaterialModal, setShowOutcomeMaterialModal] = useState(false);
  const [tableParams, setTableParams] = useState<StoreMaterialTableQuery>(
    defaultStoreMaterialTableQuery
  );
  const [parsedConfigurationItem, setParsedConfigurationItem] = useState<
    ParsedConfigurationItem | undefined
  >();
  const [showEventLogModal, setShowEventLogModal] = useState(false);

  const storeDetailQuery = useGetStoreDetailQuery(activeStoreId, tableParams);

  const storeDetail = storeDetailQuery.data;
  useRecordRecentDetail({
    type: 'warehouse',
    id: activeStoreId,
    loadedId: storeDetail?.id,
    title: storeDetail?.name,
    path: `/material/stores/${activeStoreId}`,
    isSuccess: storeDetailQuery.isSuccess
  });

  const isLoading =
    storeDetailQuery.isFetching || codebooksQuery.isLoading || companyAndUserQuery.isLoading;

  const error = storeDetailQuery.error || codebooksQuery.error || companyAndUserQuery.error;

  const refetch = async () => {
    storeDetailQuery.error && (await storeDetailQuery.refetch());
    codebooksQuery.error && (await codebooksQuery.refetch());
    companyAndUserQuery.error && (await companyAndUserQuery.refetch());
  };

  useEffect(() => {
    const parsedConfigurationItem = configuration
      ? getParsedConfigurationItem(configuration, 'storedetail')
      : undefined;

    setParsedConfigurationItem(parsedConfigurationItem);
  }, [configuration]);

  const onTableChange = (
    params: TableQuery<StoreMaterialSortBy>,
    filters: Partial<Record<string, FilterValue | null>>
  ) => {
    const updatedTableParams = {
      ...params,
      searchPhrase: tableParams.searchPhrase,
      materialCategoryId: filters.materialCategoryName?.join(','),
      materialSupplierId: filters.materialSupplierName?.join(',')
    };

    setTableParams(updatedTableParams);
  };

  const onSearch = (searchPhrase: string) => {
    if (searchPhrase === tableParams.searchPhrase) {
      return;
    }

    const updatedTableParams = {
      ...defaultStoreMaterialTableQuery,
      page: 1,
      searchPhrase: searchPhrase || undefined
    };

    setTableParams(updatedTableParams);
  };

  const handleSubmitV2 = useCallback(
    async (values: StoreFormData) => {
      try {
        await editStoreV2Mutation.mutateAsync({
          storeId: storeDetail?.id || '',
          values
        });
        setShowEditStoreV2Modal(false);

        return undefined;
      } catch (e) {
        const errors = processServerFormError(e);
        return { errors };
      }
    },
    [editStoreV2Mutation, storeDetail]
  );

  const handleDeleteStore = () => {
    return ConfirmModal.confirm({
      title: t('stores.deleteStoreTitle'),
      okText: t('misc.okText'),
      cancelText: t('misc.cancelText'),
      onCancel() {
        // do nothing
      },
      onOk: () => deleteStoreMutation.mutateAsync(storeDetail?.id || '')
    });
  };

  const pagination = {
    ...sizeChangerPaginationConfig(),
    current: tableParams.page,
    pageSize: tableParams.pageSize,
    total: storeDetail?.material.total || 0
  };

  const menuItems = [
    {
      key: 'renderSettingsDrawer',
      label: (
        <RenderSettingsDrawerWrapper
          scope="storedetail"
          type="table"
          parsedConfigurationItem={parsedConfigurationItem}
        />
      )
    },
    {
      key: 'eventlog',
      onClick: () => setShowEventLogModal(true),
      label: t('eventLog.openButton'),
      icon: <Icon path={mdiRestore} />,
      hidden: !can(['ManagerBase', 'AdminBase'])
    },
    {
      key: 'delete',
      label: t('common.delete'),
      onClick: () => handleDeleteStore(),
      icon: <Icon path={mdiDeleteOutline} />,
      hidden: !can(['Stores_DeleteAll'])
    }
  ];

  return (
    <PageContent error={error} onRetry={refetch}>
      <div className="StoreDetail_heading">
        <PageContentHeading
          title={storeDetail?.name || ''}
          actions={
            <>
              {can(['Manage', 'Stores_ManageAllQuantity']) && (
                <>
                  <Button
                    onClick={() => setShowIncomeMaterialModal(true)}
                    className="success"
                    icon={<Icon path={mdiPlus} />}
                  >
                    {t('stores.incomeMaterialButton')}
                  </Button>
                  <Button
                    onClick={() => setShowOutcomeMaterialModal(true)}
                    icon={<Icon path={mdiMinus} />}
                  >
                    {t('stores.outcomeMaterialButton')}
                  </Button>
                </>
              )}
              <SearchBox onValueChanged={onSearch} />
              {can(['Stores_ManageAll', 'Stores_ManageAllQuantity']) && (
                <Button
                  type="primary"
                  onClick={() => setShowEditStoreV2Modal(true)}
                  icon={<Icon path={mdiPencilOutline} />}
                >
                  {t('stores.editButton')}
                </Button>
              )}
              <UserCustomButtons entity="Store" entityId={storeDetail?.id} />
              <MoreDropdown items={menuItems} />
            </>
          }
        />
      </div>
      <StoreMaterialTable
        data={storeDetail?.material.data || []}
        pagination={pagination}
        isLoading={isLoading}
        onTableChange={(pagination, filters, sorter) =>
          onTableChange(
            getTableQueryFromAntdTable(pagination, sorter, { sortKeys: STORE_MATERIAL_SORT_KEYS }),
            filters
          )
        }
        selectedTableColumns={getRenderSettingsScopeTypeValue(
          'storedetail',
          'table',
          appUser,
          parsedConfigurationItem
        )}
        tableParams={tableParams}
      />
      <Modal
        open={showEditStoreV2Modal}
        onClose={() => setShowEditStoreV2Modal(false)}
        title={t('stores.editStoreTitle')}
      >
        <StoreFormV2
          onSubmit={handleSubmitV2}
          onCancel={() => setShowEditStoreV2Modal(false)}
          store={storeDetail}
        />
      </Modal>
      {showIncomeMaterialModal && (
        <MoveMaterialToStoreModal
          onClose={() => setShowIncomeMaterialModal(false)}
          open={showIncomeMaterialModal}
          selectedStoreId={activeStoreId}
          storeName={storeDetail?.name || ''}
        />
      )}
      {showOutcomeMaterialModal && (
        <MoveMaterialFromStoreModal
          open={showOutcomeMaterialModal}
          onClose={() => setShowOutcomeMaterialModal(false)}
          selectedStoreId={activeStoreId}
          storeName={storeDetail?.name || ''}
        />
      )}
      <EventLogModal
        open={showEventLogModal}
        onClose={() => setShowEventLogModal(false)}
        entityId={storeDetail?.id || ''}
        entityType="Store"
      />
    </PageContent>
  );
};

export default StoreDetail;
